import { GetStockTransactionsWithUserQuery } from '../../../../generated/generated-types';

export type StockHistoryRow = NonNullable<
  NonNullable<GetStockTransactionsWithUserQuery['getStockTransactions']>['items']
>[number];

export type StockHistorySingleDisplayItem = {
  kind: 'single';
  tx: StockHistoryRow;
  trackId: string;
};

export type StockHistoryGroupDisplayItem = {
  kind: 'group';
  key: string;
  transactions: StockHistoryRow[];
  representativeTx: StockHistoryRow;
  trackId: string;
};

export type StockHistoryDisplayItem =
  | StockHistorySingleDisplayItem
  | StockHistoryGroupDisplayItem;

/** Stable key for grouping by storage route (origin + target). */
export function storageRouteGroupKey(tx: StockHistoryRow): string {
  const originId = tx.originStoragePlaceId ?? tx.originStoragePlace?.id ?? '';
  const targetId = tx.targetStoragePlaceId ?? tx.targetStoragePlace?.id ?? '';
  return `${originId}|${targetId}`;
}

function resolveProductId(tx: StockHistoryRow): string | undefined {
  return tx.productId ?? tx.product?.id ?? undefined;
}

export function hasIdentification(tx: StockHistoryRow): boolean {
  return !!(tx.lotNumber?.trim() || tx.serialNumber?.trim());
}

/** Whether a set of identified transactions can collapse into one group card. */
export function isGroupableBucket(transactions: StockHistoryRow[]): boolean {
  if (transactions.length < 2) {
    return false;
  }
  if (!transactions.every(hasIdentification)) {
    return false;
  }
  const productId = resolveProductId(transactions[0]);
  if (!productId) {
    return false;
  }
  return transactions.every((tx) => resolveProductId(tx) === productId);
}

function preserveSortOrder(
  sortedItems: StockHistoryRow[],
  subset: StockHistoryRow[]
): StockHistoryRow[] {
  const subsetIds = new Set(subset.map((tx) => tx.id));
  return sortedItems.filter((tx) => subsetIds.has(tx.id));
}

function buildGroupsForRouteBucket(
  routeKey: string,
  bucket: StockHistoryRow[],
  sortedItems: StockHistoryRow[]
): {
  groups: StockHistoryGroupDisplayItem[];
  singleTxIds: Set<string>;
} {
  const groups: StockHistoryGroupDisplayItem[] = [];
  const singleTxIds = new Set<string>();

  for (const tx of bucket) {
    if (!hasIdentification(tx)) {
      singleTxIds.add(tx.id);
    }
  }

  const byProductId = new Map<string, StockHistoryRow[]>();
  for (const tx of bucket) {
    if (!hasIdentification(tx)) {
      continue;
    }
    const productId = resolveProductId(tx);
    if (!productId) {
      singleTxIds.add(tx.id);
      continue;
    }
    const list = byProductId.get(productId) ?? [];
    list.push(tx);
    byProductId.set(productId, list);
  }

  for (const [productId, productBucket] of byProductId) {
    const transactions = preserveSortOrder(sortedItems, productBucket);
    if (isGroupableBucket(transactions)) {
      const groupKey = `${routeKey}|${productId}`;
      groups.push({
        kind: 'group',
        key: groupKey,
        transactions,
        representativeTx: transactions[0],
        trackId: `group-${groupKey}`,
      });
    } else {
      for (const tx of transactions) {
        singleTxIds.add(tx.id);
      }
    }
  }

  return { groups, singleTxIds };
}

/**
 * Collapse groupable serial/lot rows per route into one card; non-identified rows stay single.
 */
export function buildStockHistoryDisplayItems(
  sortedItems: StockHistoryRow[]
): StockHistoryDisplayItem[] {
  const routeBuckets = new Map<string, StockHistoryRow[]>();
  for (const tx of sortedItems) {
    const routeKey = storageRouteGroupKey(tx);
    const bucket = routeBuckets.get(routeKey) ?? [];
    bucket.push(tx);
    routeBuckets.set(routeKey, bucket);
  }

  const txIdToGroup = new Map<string, StockHistoryGroupDisplayItem>();
  const singleTxIds = new Set<string>();

  for (const [routeKey, bucket] of routeBuckets) {
    const { groups, singleTxIds: routeSingles } = buildGroupsForRouteBucket(
      routeKey,
      bucket,
      sortedItems
    );
    for (const group of groups) {
      for (const tx of group.transactions) {
        txIdToGroup.set(tx.id, group);
      }
    }
    for (const id of routeSingles) {
      singleTxIds.add(id);
    }
  }

  const emittedGroupTrackIds = new Set<string>();
  const result: StockHistoryDisplayItem[] = [];

  for (const tx of sortedItems) {
    const group = txIdToGroup.get(tx.id);
    if (group && !singleTxIds.has(tx.id)) {
      if (emittedGroupTrackIds.has(group.trackId)) {
        continue;
      }
      emittedGroupTrackIds.add(group.trackId);
      result.push(group);
      continue;
    }
    result.push({
      kind: 'single',
      tx,
      trackId: tx.id,
    });
  }

  return result;
}
